import React, { useCallback, useEffect, useState } from 'react'
import { Card, Table, Form, Badge, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import useTitle from 'hooks/useTitle'
import apiService from 'Service'
import LoadingSpinner from 'Components/LoadingSpinner'
import FiscalAlertsBanner from 'Pages/Dashboard/FiscalAlertsBanner'
import companyStore from 'stores/companyStore'
import { getFiscalObligationLabel } from 'Utilities/fiscalObligations'
import { formatCivilDate, todayCivil } from 'Utilities/dateTime'

const FiscalCalendarCompany = () => {
  const { t } = useTranslation()
  useTitle('fiscal_calendar')
  const [year, setYear] = useState(() => Number(todayCivil().slice(0, 4)))
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    apiService.FiscalCalendar.getCompanySchedule(year)
      .then(data => setEntries(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false))
  }, [year])

  useEffect(() => { load() }, [load])

  const rifDigit = companyStore.selectedCompany?.Rif?.slice(-1)
    ?? entries[0]?.RifLastDigit

  const toggle = (entry) => {
    const req = entry.IsCompleted
      ? apiService.FiscalCalendar.unmarkCompleted(entry.EntryId)
      : apiService.FiscalCalendar.markCompleted(entry.EntryId)
    req.then(() => load())
  }

  return (
    <div>
      <h4>{t('fiscal_calendar')}</h4>
      <p className="text-muted">
        {t('fiscal_calendar_company_hint') || 'Obligaciones filtradas por el dígito terminal de su RIF.'}
        {rifDigit != null && <> <Badge bg="secondary">RIF ·{rifDigit}</Badge></>}
      </p>

      <FiscalAlertsBanner />

      <Card className="mb-3">
        <Card.Body className="d-flex align-items-center gap-3">
          <Form.Label className="mb-0">{t('year') || 'Año'}</Form.Label>
          <Form.Control
            type="number"
            style={{ maxWidth: 120 }}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
          />
          <Button variant="outline-primary" onClick={load}>{t('refresh') || 'Actualizar'}</Button>
        </Card.Body>
      </Card>

      {loading && <LoadingSpinner />}

      {!loading && entries.length === 0 && (
        <p className="text-center text-muted">{t('no_upcoming_obligations')}</p>
      )}

      {!loading && entries.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t('completed') || 'Cumplida'}</th>
              <th>{t('obligation') || 'Obligación'}</th>
              <th>{t('period') || 'Periodo'}</th>
              <th>{t('due_date') || 'Vencimiento'}</th>
              <th>{t('days_left') || 'Días'}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.EntryId} className={entry.IsCompleted ? 'table-success' : ''}>
                <td>
                  <Form.Check checked={entry.IsCompleted} onChange={() => toggle(entry)} />
                </td>
                <td>{getFiscalObligationLabel(entry.ObligationType, t)}</td>
                <td>{String(entry.Month).padStart(2, '0')}/{entry.Year}</td>
                <td>{formatCivilDate(entry.DueDate)}</td>
                <td>
                  {entry.IsCompleted ? '—' : (
                    entry.DaysUntilDue < 0
                      ? <Badge bg="danger">{t('overdue') || 'Vencida'}</Badge>
                      : entry.DaysUntilDue
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  )
}

export default FiscalCalendarCompany
